/**
 * Search Expression Functions
 *
 * Dictionary search, result insertion, and popup management.
 */

import { appState } from '../state/AppState';
import { session } from './puzzleSession';
import { updateHistoryBar } from './menuUpdates';

/**
 * Search for an expression in the dictionary.
 * Makes a POST request to /kereses and updates the target element with results.
 *
 * @param pattern - The search pattern (minta)
 * @param expression - The full expression (kifejezes)
 * @param targetElement - The DOM element to update with search results
 */
export function searchExpression(
    pattern: string,
    expression: string,
    targetElement: HTMLElement,
): void {
    const params = `minta=${encodeURIComponent(
        pattern,
    )}&kifejezes=${encodeURIComponent(expression)}`;

    fetch('/kereses', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params,
    })
        .then((response) => response.text())
        .then((html) => {
            targetElement.innerHTML = html;

            // Add insert/cancel buttons if not already present
            if (!document.getElementById('keresesGomb')) {
                const parent = targetElement.parentNode as HTMLElement;
                if (parent) {
                    const buttonsHtml =
                        '</div><div class="input-block" id="keresesGomb">' +
                        '<input type="button" id="searchInsertBtnExt" value="Beszúrás">' +
                        '<input type="reset" id="searchCancelBtnExt" value="Mégse">';
                    parent.insertAdjacentHTML('beforeend', buttonsHtml);

                    // Attach event handlers
                    const insertBtn =
                        document.getElementById('searchInsertBtnExt');
                    const cancelBtn =
                        document.getElementById('searchCancelBtnExt');

                    if (insertBtn) {
                        insertBtn.addEventListener('click', () =>
                            insertSearchResultExternal(),
                        );
                    }
                    if (cancelBtn) {
                        cancelBtn.addEventListener('click', () =>
                            hidePopupExternal(),
                        );
                    }
                }
            }

            // Update popup position if function exists
            if (
                typeof (window as unknown as { updatePopup?: () => void })
                    .updatePopup === 'function'
            ) {
                (
                    window as unknown as { updatePopup: () => void }
                ).updatePopup();
            }
        })
        .catch((error) => {
            console.error('Search error:', error);
            targetElement.innerHTML = 'Hiba a keresés során';
        });
}

/**
 * Insert search result into puzzle (external search function).
 * Like legacy keresesBeszuras.
 */
function insertSearchResultExternal(): void {
    const expressionSelect = document.getElementById(
        'selectKifejezesek',
    ) as HTMLSelectElement;
    const clueSelect = document.getElementById(
        'selectMeghatarozasok',
    ) as HTMLSelectElement;

    if (
        !expressionSelect ||
        !clueSelect ||
        !session.currentPuzzle ||
        !session.historyManager
    )
        return;

    const expression =
        expressionSelect.options[expressionSelect.selectedIndex]?.value;
    const clue = clueSelect.options[clueSelect.selectedIndex]?.value;

    // Get the full selection (currentSelection + currentCellId, sorted)
    const selection = [...appState.currentSelection];
    if (!selection.includes(appState.currentCellId)) {
        selection.push(appState.currentCellId);
    }
    selection.sort((a, b) => a - b);

    // Set expression to cells
    if (expression) {
        const chars = expression.split('');
        for (let i = 0; i < selection.length && i < chars.length; i++) {
            const cell = session.currentPuzzle.getCell(selection[i]);
            if (cell && !cell.isBlack && !cell.isImage && !cell.isEmpty) {
                cell.setValue(chars[i]);
                const cellEl = document.getElementById(`mezo-${selection[i]}`);
                if (cellEl) {
                    cellEl.innerHTML = chars[i];
                }
            }
        }
    }

    // Set clue to first cell
    if (clue && selection.length > 0) {
        const firstCell = session.currentPuzzle.getCell(selection[0]);
        if (firstCell) {
            // Get expression from selection
            const regex = new RegExp(appState.validCharPattern, 'g');
            const expr = selection
                .map((id) => {
                    const c = session.currentPuzzle!.getCell(id);
                    const v = c?.value;
                    if (v && regex.test(v)) {
                        regex.lastIndex = 0;
                        return v;
                    }
                    regex.lastIndex = 0;
                    return '_';
                })
                .join('');

            if (appState.isHorizontal) {
                firstCell.setVerticalClue(clue);
                if (firstCell.hasVerticalClue) {
                    firstCell.setVerticalExpression(expr);
                }
            } else {
                firstCell.setHorizontalClue(clue);
                if (firstCell.hasHorizontalClue) {
                    firstCell.setHorizontalExpression(expr);
                }
            }

            // Update background image
            const cellEl = document.getElementById(`mezo-${selection[0]}`);
            if (cellEl) {
                cellEl.style.backgroundImage = firstCell.getBackgroundImage(
                    appState.isHorizontal,
                    !appState.clueMarking,
                );
            }
        }
    }

    session.historyManager.save('Kifejezés beszúrása');
    updateHistoryBar();
    hidePopupExternal();
    appState.currentElement?.focus();
}

/**
 * Hide popup (external search function).
 * Like legacy hidePopup.
 */
function hidePopupExternal(): void {
    const popup = document.getElementById('rejtveny_popupmenu');
    const zarFixMenu = document.getElementById('zarFixMenu');
    const zarRejtveny = document.getElementById('zarRejtveny');
    const zarPopUp = document.getElementById('zarPopUp');
    const component = document.getElementById('rejtvenyKomponens');

    if (popup) popup.style.display = 'none';
    if (zarFixMenu) zarFixMenu.style.display = 'block';
    if (zarRejtveny) zarRejtveny.style.display = 'block';
    if (zarPopUp) zarPopUp.style.display = 'none';
    if (component) component.style.position = 'relative';

    appState.currentElement?.focus();
}
